/**
 * Facade rules per design code. Consumed by <FacadeBadges/> on web and
 * MobileFacadeRulesModal on mobile so both surfaces stay in lock-step.
 *
 * Structure: RULES[code][kind][lang] = { title, lines, refs }
 * kind ∈ {street, neighbor, open}
 * code ∈ {SBC_304, ACI_318, EUROCODE_2, BS_8110, IS_456, ECP_203}
 * lang ∈ {ar, en}
 *
 * When a design code is unknown / unspecified, fall back to SBC_304.
 */

export const CODE_LABELS = {
  SBC_304:    { ar: "🇸🇦 SBC 304",         en: "🇸🇦 SBC 304 (Saudi)"    },
  ACI_318:    { ar: "🇺🇸 ACI 318",         en: "🇺🇸 ACI 318 (US)"       },
  EUROCODE_2: { ar: "🇪🇺 EC 2",            en: "🇪🇺 Eurocode 2"         },
  BS_8110:    { ar: "🇬🇧 BS 8110",         en: "🇬🇧 BS 8110 (UK)"       },
  IS_456:     { ar: "🇮🇳 IS 456 · NBC",    en: "🇮🇳 IS 456 · NBC"       },
  ECP_203:    { ar: "🇪🇬 ECP 203",         en: "🇪🇬 ECP 203 (Egypt)"    },
};

export const RULES = {
  SBC_304: {
    street: {
      ar: {
        title: "🛣️ واجهة على شارع",
        lines: [
          "• واجهة المبنى الرئيسية - تُوضع فيها المداخل والشرفات.",
          "• الفتحات (نوافذ + أبواب) مسموحة بحرية.",
          "• الارتداد الأدنى: 3.00–6.00 م حسب البلدية.",
        ],
        refs: ["SBC 201 §5.2.3", "SBC 801 §303.1"],
      },
      en: {
        title: "🛣️ Street-facing facade",
        lines: [
          "• Primary facade - hosts entries and balconies.",
          "• Windows and doors freely allowed.",
          "• Minimum street setback: 3.00–6.00 m per municipality.",
        ],
        refs: ["SBC 201 §5.2.3", "SBC 801 §303.1"],
      },
    },
    neighbor: {
      ar: {
        title: "🧱 جار (جدار مشترك)",
        lines: [
          "• الفتحات ممنوعة تماماً على جدار مشترك مع الجار.",
          "• جدار كتيم - مقاومة حريق ≥ 2 ساعة.",
          "• التهوية والإنارة عبر مناور داخلية.",
          "• عزل صوتي ≥ Rw 55 dB.",
        ],
        refs: ["SBC 201 §5.4", "SBC 801 §704.1"],
      },
      en: {
        title: "🧱 Neighbor party-wall",
        lines: [
          "• Openings strictly forbidden on party walls.",
          "• Solid wall - fire rating ≥ 2 hours.",
          "• Daylight/vent via internal light-wells.",
          "• Acoustic rating ≥ Rw 55 dB.",
        ],
        refs: ["SBC 201 §5.4", "SBC 801 §704.1"],
      },
    },
    open: {
      ar: {
        title: "🌤️ هواء طلق",
        lines: [
          "• الفتحات مسموحة بأحجام مريحة.",
          "• ارتداد أدنى: 2.50 م.",
          "• شرفات وتراسات جائزة إذا كان الارتداد ≥ 3.00 م.",
        ],
        refs: ["SBC 201 §5.3"],
      },
      en: {
        title: "🌤️ Open-air",
        lines: [
          "• Generous openings allowed.",
          "• Minimum setback: 2.50 m.",
          "• Balconies/terraces allowed where setback ≥ 3.00 m.",
        ],
        refs: ["SBC 201 §5.3"],
      },
    },
  },

  ACI_318: {
    street: {
      ar: {
        title: "🛣️ واجهة على شارع (كود أمريكي)",
        lines: [
          "• ارتداد يحدده الـ Zoning المحلي (نموذجيًا 6.00–7.60 م).",
          "• الفتحات مسموحة - تخضع لمتطلبات الطاقة IECC.",
          "• شرفات مسموحة ضمن مسافة الارتداد.",
        ],
        refs: ["IBC §303", "IECC §C402", "ACI 318 §26.3"],
      },
      en: {
        title: "🛣️ Street facade (US code)",
        lines: [
          "• Setback per local zoning (typ. 20–25 ft).",
          "• Openings allowed - governed by IECC energy requirements.",
          "• Balconies allowed within the setback envelope.",
        ],
        refs: ["IBC §303", "IECC §C402", "ACI 318 §26.3"],
      },
    },
    neighbor: {
      ar: {
        title: "🧱 جار (Lot line - كود أمريكي)",
        lines: [
          "• الفتحات ممنوعة إذا كان الجدار على حد الملكية.",
          "• جدار خارجي على حد الملكية: مقاومة حريق 2 ساعة.",
          "• الفتحات مسموحة عند مسافة ≥ 3 ft (0.90 م) بحد أقصى 15% من مساحة الجدار.",
          "• عند مسافة ≥ 5 ft (1.50 م): 25%; ≥ 10 ft (3.00 م): بلا حد.",
        ],
        refs: ["IBC §705.8", "ACI 318 §26.3", "NFPA 5000 §7.3"],
      },
      en: {
        title: "🧱 Neighbor lot-line (US code)",
        lines: [
          "• Openings prohibited when wall is on the property line.",
          "• Exterior wall on lot line: 2-hour fire-resistance rating.",
          "• Openings allowed at ≥ 3 ft (0.9 m) capped at 15% of wall area.",
          "• At ≥ 5 ft (1.5 m): 25%; ≥ 10 ft (3 m): unlimited.",
        ],
        refs: ["IBC §705.8", "ACI 318 §26.3", "NFPA 5000 §7.3"],
      },
    },
    open: {
      ar: {
        title: "🌤️ هواء طلق (كود أمريكي)",
        lines: [
          "• Side yard ≥ 5 ft (1.50 م) للسكني R-3.",
          "• شرفات مسموحة عند yard ≥ 10 ft (3.00 م).",
        ],
        refs: ["IBC §705.3", "IRC §R302"],
      },
      en: {
        title: "🌤️ Open-air (US code)",
        lines: [
          "• Side yard ≥ 5 ft (1.5 m) for R-3 residential.",
          "• Balconies where yard ≥ 10 ft (3 m).",
        ],
        refs: ["IBC §705.3", "IRC §R302"],
      },
    },
  },

  EUROCODE_2: {
    street: {
      ar: {
        title: "🛣️ واجهة على شارع (Eurocode)",
        lines: [
          "• الارتداد بحسب اللائحة المحلية للدولة العضو.",
          "• الفتحات + العزل الحراري بحسب EN ISO 13790.",
          "• الأحمال الديناميكية (رياح + زلزال) بحسب EN 1991 و EN 1998.",
        ],
        refs: ["EN 1992-1-1 §3", "EN 1996 §8", "EN ISO 13790"],
      },
      en: {
        title: "🛣️ Street facade (Eurocode)",
        lines: [
          "• Setback per member-state's national annex.",
          "• Openings + thermal envelope per EN ISO 13790.",
          "• Wind/seismic loads per EN 1991 & EN 1998.",
        ],
        refs: ["EN 1992-1-1 §3", "EN 1996 §8", "EN ISO 13790"],
      },
    },
    neighbor: {
      ar: {
        title: "🧱 جار (Eurocode)",
        lines: [
          "• الفتحات ممنوعة على جدار مشترك.",
          "• مقاومة حريق EI 90 → EI 120 حسب فئة المبنى.",
          "• عزل صوتي DnT,w ≥ 53 dB (EN 12354-1).",
        ],
        refs: ["EN 1996-1-2", "EN 12354-1", "EN 1992-1-2"],
      },
      en: {
        title: "🧱 Neighbor party-wall (Eurocode)",
        lines: [
          "• Openings forbidden on party walls.",
          "• Fire resistance EI 90 → EI 120 by occupancy class.",
          "• Acoustic DnT,w ≥ 53 dB (EN 12354-1).",
        ],
        refs: ["EN 1996-1-2", "EN 12354-1", "EN 1992-1-2"],
      },
    },
    open: {
      ar: {
        title: "🌤️ هواء طلق (Eurocode)",
        lines: [
          "• الفتحات مسموحة، متطلبات U-value بحسب اللائحة الوطنية.",
          "• شرفات جائزة مع تصميم أحمال EN 1991-1-1.",
        ],
        refs: ["EN 1992-1-1", "EN 1991-1-1"],
      },
      en: {
        title: "🌤️ Open-air (Eurocode)",
        lines: [
          "• Openings allowed; U-values per national annex.",
          "• Balconies designed to EN 1991-1-1 load class.",
        ],
        refs: ["EN 1992-1-1", "EN 1991-1-1"],
      },
    },
  },

  BS_8110: {
    street: {
      ar: {
        title: "🛣️ واجهة على شارع (BS/UK)",
        lines: [
          "• ارتداد بحسب Local Planning Authority.",
          "• الفتحات مسموحة - Part L لعزل الحرارة.",
        ],
        refs: ["BS 8110-1 §3", "Approved Doc L"],
      },
      en: {
        title: "🛣️ Street facade (UK - BS/BR)",
        lines: [
          "• Setback per Local Planning Authority.",
          "• Openings allowed - Part L for thermal envelope.",
        ],
        refs: ["BS 8110-1 §3", "Approved Doc L"],
      },
    },
    neighbor: {
      ar: {
        title: "🧱 جار (BS/UK)",
        lines: [
          "• الفتحات ممنوعة على جدار الملكية.",
          "• مقاومة حريق ≥ 60–120 دقيقة حسب فئة الاستخدام.",
          "• Party Wall Act 1996 - إشعار الجار قبل البناء.",
        ],
        refs: ["Approved Doc B §5.1", "BS 8110-1", "Party Wall Act 1996"],
      },
      en: {
        title: "🧱 Neighbor party-wall (UK)",
        lines: [
          "• Openings forbidden on property boundary.",
          "• Fire resistance ≥ 60–120 min by occupancy class.",
          "• Party Wall Act 1996 - notify neighbor before works.",
        ],
        refs: ["Approved Doc B §5.1", "BS 8110-1", "Party Wall Act 1996"],
      },
    },
    open: {
      ar: {
        title: "🌤️ هواء طلق (BS/UK)",
        lines: [
          "• الفتحات مسموحة.",
          "• Right to Light - 45° من نافذة الجار.",
        ],
        refs: ["Approved Doc B", "Rights of Light Act 1959"],
      },
      en: {
        title: "🌤️ Open-air (UK)",
        lines: [
          "• Openings freely allowed.",
          "• Right to Light - 45° cone from neighbor's window.",
        ],
        refs: ["Approved Doc B", "Rights of Light Act 1959"],
      },
    },
  },

  IS_456: {
    street: {
      ar: {
        title: "🛣️ واجهة على شارع (NBC India)",
        lines: [
          "• الارتداد 3.00–4.50 م حسب عرض الشارع (NBC 2016).",
          "• الفتحات ≥ 12.5% من مساحة الأرضية (تهوية).",
        ],
        refs: ["IS 456 §26", "NBC 2016 §3.3", "NBC §33"],
      },
      en: {
        title: "🛣️ Street facade (NBC India)",
        lines: [
          "• Setback 3.00–4.50 m by street width (NBC 2016).",
          "• Openings ≥ 12.5% of floor area for daylight/vent.",
        ],
        refs: ["IS 456 §26", "NBC 2016 §3.3", "NBC §33"],
      },
    },
    neighbor: {
      ar: {
        title: "🧱 جار (NBC India)",
        lines: [
          "• الفتحات ممنوعة على جدار الملكية.",
          "• مقاومة حريق 2 ساعة (Group A/B).",
          "• التهوية عبر مناور ≥ 3.0 م² (NBC §31).",
        ],
        refs: ["IS 456 §26.3", "NBC 2016 §3.4", "NBC §31"],
      },
      en: {
        title: "🧱 Neighbor party-wall (NBC India)",
        lines: [
          "• Openings forbidden on property boundary.",
          "• 2-hour fire resistance (Group A/B).",
          "• Vent via light-well ≥ 3.0 m² (NBC §31).",
        ],
        refs: ["IS 456 §26.3", "NBC 2016 §3.4", "NBC §31"],
      },
    },
    open: {
      ar: {
        title: "🌤️ هواء طلق (NBC India)",
        lines: [
          "• ارتداد 1.00–3.00 م حسب فئة المبنى.",
          "• Chajja + Balcony جائزة.",
        ],
        refs: ["NBC 2016 §3.3.4"],
      },
      en: {
        title: "🌤️ Open-air (NBC India)",
        lines: [
          "• Setback 1.00–3.00 m by building class.",
          "• Chajja + balcony projections allowed.",
        ],
        refs: ["NBC 2016 §3.3.4"],
      },
    },
  },

  ECP_203: {
    street: {
      ar: {
        title: "🛣️ واجهة على شارع (كود مصري)",
        lines: [
          "• الارتداد بحسب اشتراطات الحي وقانون البناء 119/2008.",
          "• الفتحات مسموحة، عرض الشارع يحدد الارتفاع.",
        ],
        refs: ["ECP 203 §3.4", "قانون 119/2008"],
      },
      en: {
        title: "🛣️ Street facade (Egypt)",
        lines: [
          "• Setback per district + Building Law 119/2008.",
          "• Openings allowed; street width caps height.",
        ],
        refs: ["ECP 203 §3.4", "Law 119/2008"],
      },
    },
    neighbor: {
      ar: {
        title: "🧱 جار (كود مصري)",
        lines: [
          "• الفتحات ممنوعة على جدار الجار.",
          "• مقاومة حريق ≥ 2 ساعة.",
          "• التهوية عبر منور مساحة ≥ 6% من مساحة الغرف المخدومة.",
        ],
        refs: ["ECP 203 §7", "ECP 204", "قانون 119/2008 §13"],
      },
      en: {
        title: "🧱 Neighbor party-wall (Egypt)",
        lines: [
          "• Openings forbidden on party wall.",
          "• Fire resistance ≥ 2 hours.",
          "• Ventilation via light-well area ≥ 6% of served rooms.",
        ],
        refs: ["ECP 203 §7", "ECP 204", "Law 119/2008 §13"],
      },
    },
    open: {
      ar: {
        title: "🌤️ هواء طلق (كود مصري)",
        lines: [
          "• الفتحات مسموحة.",
          "• شرفات جائزة عند ارتداد ≥ 3.00 م.",
        ],
        refs: ["ECP 203 §3.5"],
      },
      en: {
        title: "🌤️ Open-air (Egypt)",
        lines: [
          "• Openings freely allowed.",
          "• Balconies at setback ≥ 3.00 m.",
        ],
        refs: ["ECP 203 §3.5"],
      },
    },
  },
};

/** Fallback safely to SBC_304 when caller passes an unknown code. */
export function rulesFor(code, kind, lang = "ar") {
  const c = RULES[code] ? code : "SBC_304";
  const k = ["street", "neighbor", "open"].includes(kind) ? kind : "open";
  const l = lang === "en" ? "en" : "ar";
  return RULES[c][k][l];
}
